// ink-component
import React, { ReactElement } from 'react';
import { formatNumber } from '@carta/pen';

import getGlobalProps from '../../helpers/constants/global-props';

import getNumberValue from '../_common/helpers/getNumberValue';

export interface FormattedNumberProps {
  /** Original value to be formatted */
  value?: string | number;
  /** Custom formatting string based on the `numeral` library */
  format?: string;
  /** Optional prefix */
  prefix?: string;
  /** Optional suffix */
  suffix?: string;
  /** Custom rounding function based on the Math JS Object */
  rounding?: 'round' | 'ceil' | 'floor';
  /** Optional custom error node */
  error?: ReactElement;
  /** Option for capitalization of the formatted value. Does not capitalize suffixes and prefixes. */
  capitalize?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  currency?: boolean;
  /** @deprecated:none Zeros are now always shown by default */
  allowZero?: boolean;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Formats your numbers with ease
 * @group Formatted
 * @categories formatter
 */
function FormattedNumber(props: FormattedNumberProps) {
  const { value, format = '', prefix, suffix, currency, capitalize, error, rounding } = props;

  const adjustedValue = getNumberValue(value);

  function getFormattedValue(numberValue: number) {
    let formattedValue = `${prefix}${formatNumber(numberValue, format, { rounding })}${suffix}`;

    // As this is the only place that we actually format the value, I had to add an invisible internal
    // prop called `currency` to change the value of the currency symbol *after* the format occurs
    // in monetary situations.
    // TODO: This will probably change once we use a different formatting library (numbro)
    if (currency && prefix) {
      formattedValue = `${formatNumber(numberValue, format, { rounding })}${suffix}`.replace('$', prefix);
    }

    // As this is the only place that we actually format the value, I had to add the treatment of
    // capitalization here.
    // TODO: This will probably change once we use a different formatting library (numbro)
    if (capitalize) {
      formattedValue = `${prefix}${formatNumber(numberValue, format, { rounding }).toUpperCase()}${suffix}`;
    }

    return formattedValue;
  }

  if (adjustedValue === undefined) {
    return error ? <>{error}</> : null;
  }

  return (
    <span data-testid={props['data-testid']} {...globalProps}>
      {getFormattedValue(adjustedValue)}
    </span>
  );
}

FormattedNumber.defaultProps = {
  value: '',
  format: '',
  prefix: '',
  suffix: '',
  error: undefined,
  capitalize: false,
  'data-testid': undefined,
  allowZero: false,
  currency: false,
};

FormattedNumber.displayName = 'FormattedNumber';

export default FormattedNumber;
